import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import { getInsightsAvailability, getAssistSurveys } from '@/api/client';
import type { AssistEngineSurvey } from '../types';

interface InsightsNavState {
  hasResults: boolean;
  hasActiveSurvey: boolean;
  hasCompletedSurvey: boolean;
  /**
   * Whether the tenant's DEFAULT composed view configures any driver-type
   * modules. Derived from the backend's cheap availability check. Defaults to
   * true so pages that gate "calculating spinner" on this signal preserve
   * legacy behavior if the availability endpoint failed or hasn't loaded yet.
   *
   * SCOPE — read this before using it. The fetch below passes no survey id, so
   * this describes the tenant's composed default (each module from the latest
   * completed survey that included it), NOT whichever survey a page has pinned
   * via `?survey=`. That is the right signal for the tenant-wide readers — the
   * sidebar's `segmentable` gate, ActionsPage, TeamsPage, none of which have a
   * picker. It is the WRONG signal for a page that pins: driver/KPI shape
   * differs per survey, so a pinned survey would be explained with the default
   * survey's shape (EL-1135). Picker-bearing pages use
   * `useSurveyAvailability(surveyId)` instead, which falls back to these exact
   * values when nothing is pinned.
   */
  surveyHasDrivers: boolean;
  /** Mirror of surveyHasDrivers, same scope caveat, for kpi-type modules. */
  surveyHasKpis: boolean;
  /**
   * True when the tenant's overview HAS been computed but its pooled population
   * is below the anonymity threshold — i.e. a completed survey exists yet can't
   * produce results for lack of responses (EL-687). Pages use this to show a
   * final "not enough responses yet" message instead of an endless "calculating"
   * spinner. Deliberately requires the overview to have been computed
   * (`computed_at` set): a never-computed tenant is genuinely still calculating,
   * so this stays false there and the spinner is preserved. Defaults to false
   * when availability is absent/failed, keeping the legacy spinner UX.
   */
  resultsBelowThreshold: boolean;
  /**
   * The survey a results-less tenant should be forwarded into (EL-673): the
   * most relevant survey to open on its Participation tab when the overview
   * has nothing to show yet. `null` when the tenant has no (non-archived)
   * survey at all. Prefers an in-progress survey, then the most recently
   * created one.
   */
  firstSurveyId: string | null;
  /**
   * The tenant's scheduled-or-in-progress survey, if any (EL-788). Deliberately
   * narrower than `hasActiveSurvey` above (which is also true for
   * completed/ended surveys — it drives the Participation nav gate): this
   * signal answers "is there a survey the tenant just launched or is about to
   * launch". Backs the Overview page's post-launch confirmation banner and
   * lets the Overview nav item unlock before any survey has completed. Prefers
   * an in-progress (live) survey over a merely scheduled one; ties break on
   * the most recently created. `null` when nothing is scheduled or running.
   */
  scheduledOrLiveSurvey: AssistEngineSurvey | null;
  /**
   * How many surveys have reached a completed status ('completed' or 'ended').
   * Used by the home page lifecycle stage resolver to distinguish "first results"
   * (one completed survey with no trend data) from "established" (multiple surveys
   * allow trend comparison). Computed from the same survey list as `hasCompletedSurvey`.
   *
   * `null` means "not known" (the survey fetch failed or hasn't resolved yet) —
   * distinct from `0`, a genuine "no completed surveys". Conflating the two used
   * to make the stage resolver tell an established tenant with years of survey
   * history that they had none, whenever a transient network error hit only this
   * fetch (the score fetch is separate and can still succeed).
   */
  completedSurveyCount: number | null;
  /**
   * Every survey the tenant's list endpoint returned, in wire order.
   *
   * The derived booleans above answer "does a survey of kind X exist"; the
   * stage-driven `/insights` home needs the survey OBJECTS themselves — the
   * awaiting stage fetches participation for the survey that just closed, the
   * established dashboard lists every live survey with its own progress, and
   * several stage headers name the survey. That list is already fetched here
   * for `hasCompletedSurvey` and friends, so exposing it costs nothing;
   * fetching it again from the page would double an identical request.
   *
   * `[]` when the fetch failed — indistinguishable from "the tenant has no
   * surveys", so callers that must tell those apart pair it with `loading`.
   */
  surveys: AssistEngineSurvey[];
  /**
   * This tenant's k-anonymity floor (backend's max(5, ...)-floored
   * InsightConfig.anonymity_threshold), sourced from the availability
   * response. Defaults to 5 — the platform-wide floor — so copy that reads
   * this value (e.g. the Launched-stage locked-results card) never has to
   * hardcode it, including while availability hasn't loaded yet or failed.
   */
  anonymityThreshold: number;
  /**
   * True until the SURVEY-STATUS fetch resolves. This — and only this — gates
   * the sidebar menu (AppSidebar reads `hasCompletedSurvey && !loading`).
   *
   * Critically, `loading` does NOT wait on the availability/results fetch.
   * Previously the menu blocked on the heavy company-scores aggregation; now
   * the cheap survey-status list alone decides when the menu un-grays, and the
   * results signal (`hasResults`) lands independently a moment later.
   */
  loading: boolean;
}

const InsightsNavContext = createContext<InsightsNavState>({
  hasResults: false,
  hasActiveSurvey: false,
  hasCompletedSurvey: false,
  surveyHasDrivers: true,
  surveyHasKpis: true,
  resultsBelowThreshold: false,
  firstSurveyId: null,
  scheduledOrLiveSurvey: null,
  completedSurveyCount: null,
  surveys: [],
  anonymityThreshold: 5,
  loading: true,
});

export const useInsightsNav = () => useContext(InsightsNavContext);

const ACTIVE_STATUSES = new Set(['active', 'published', 'in_progress', 'completed', 'ended']);
const COMPLETED_STATUSES = new Set(['completed', 'ended']);
const REDIRECT_PRIORITY_STATUSES = new Set(['active', 'in_progress', 'published']);
const LAUNCHING_STATUSES = new Set(['scheduled', 'in_progress']);

/**
 * Pick the survey to forward a results-less tenant into (EL-673). Skips
 * archived surveys; prefers an in-progress one, then the most recently
 * created. Returns null when there's nothing to open.
 */
function pickFirstSurveyId(
  surveys: { id: string; ui_status: string; created_at?: string | null }[] | undefined,
): string | null {
  const candidates = (surveys ?? []).filter((s) => s.ui_status !== 'archived');
  if (candidates.length === 0) return null;
  const sorted = [...candidates].sort((a, b) => {
    const ap = REDIRECT_PRIORITY_STATUSES.has(a.ui_status) ? 0 : 1;
    const bp = REDIRECT_PRIORITY_STATUSES.has(b.ui_status) ? 0 : 1;
    if (ap !== bp) return ap - bp;
    return (b.created_at ?? '').localeCompare(a.created_at ?? ''); // newest first
  });
  return sorted[0]?.id ?? null;
}

/**
 * Pick the tenant's scheduled-or-in-progress survey (EL-788): the survey the
 * Overview page's post-launch banner confirms. Prefers a LIVE (in_progress)
 * survey over a merely scheduled one; ties break on the most recently
 * created. Returns null when nothing is scheduled or running.
 */
function pickScheduledOrLiveSurvey(
  surveys: AssistEngineSurvey[] | undefined,
): AssistEngineSurvey | null {
  const candidates = (surveys ?? []).filter((s) => LAUNCHING_STATUSES.has(s.ui_status));
  if (candidates.length === 0) return null;
  const sorted = [...candidates].sort((a, b) => {
    const ap = a.ui_status === 'in_progress' ? 0 : 1;
    const bp = b.ui_status === 'in_progress' ? 0 : 1;
    if (ap !== bp) return ap - bp;
    return (b.created_at ?? '').localeCompare(a.created_at ?? ''); // newest first
  });
  return sorted[0] ?? null;
}

export function InsightsNavProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<InsightsNavState>({
    hasResults: false,
    hasActiveSurvey: false,
    hasCompletedSurvey: false,
    surveyHasDrivers: true,
    surveyHasKpis: true,
    resultsBelowThreshold: false,
    firstSurveyId: null,
    scheduledOrLiveSurvey: null,
    completedSurveyCount: null,
    surveys: [],
    anonymityThreshold: 5,
    loading: true,
  });

  useEffect(() => {
    let cancelled = false;

    // Two INDEPENDENT fetches, deliberately not Promise.all'd:
    //
    //  1. survey statuses (cheap) → decide hasActiveSurvey/hasCompletedSurvey
    //     and clear `loading` so the sidebar menu un-grays immediately.
    //  2. availability (cheap point-read) → hasResults + module-type flags,
    //     used by pages for the "calculating" vs "no such module" UX.
    //
    // The sidebar must never wait on (2). Each updates only its own slice of
    // state via a functional setState so the two can resolve in any order.

    async function loadSurveys() {
      const surveys = await getAssistSurveys().catch(() => null);
      if (cancelled) return;
      const hasActiveSurvey = surveys?.surveys?.some(s => ACTIVE_STATUSES.has(s.ui_status)) ?? false;
      const hasCompletedSurvey = surveys?.surveys?.some(s => COMPLETED_STATUSES.has(s.ui_status)) ?? false;
      // `null` (not `0`) when the fetch itself failed/returned nothing — see
      // the field doc comment above. Only a resolved list yields a real count.
      const completedSurveyCount = surveys?.surveys
        ? surveys.surveys.filter(s => COMPLETED_STATUSES.has(s.ui_status)).length
        : null;
      const firstSurveyId = pickFirstSurveyId(surveys?.surveys);
      const scheduledOrLiveSurvey = pickScheduledOrLiveSurvey(surveys?.surveys);
      setState(prev => ({
        ...prev,
        hasActiveSurvey,
        hasCompletedSurvey,
        completedSurveyCount,
        firstSurveyId,
        scheduledOrLiveSurvey,
        surveys: surveys?.surveys ?? [],
        loading: false,
      }));
    }

    async function loadAvailability() {
      const availability = await getInsightsAvailability().catch(() => null);
      if (cancelled) return;
      const hasResults = availability?.has_results ?? false;
      // Fall back to true if availability failed/absent so callers keep the
      // legacy "show calculating spinner" UX rather than silently hiding the
      // section.
      const surveyHasDrivers = availability?.has_driver_modules ?? true;
      const surveyHasKpis = availability?.has_kpi_modules ?? true;
      // "Computed but below threshold" — a final "too few responses" state, not
      // "still calculating". Gate on computed_at so a never-computed tenant (or
      // a failed/absent availability fetch) keeps the spinner: meets_threshold's
      // False default must not be mistaken for a real below-threshold verdict.
      const resultsBelowThreshold =
        !!availability &&
        availability.computed_at != null &&
        availability.meets_threshold === false;
      const anonymityThreshold = availability?.anonymity_threshold ?? 5;
      setState(prev => ({
        ...prev,
        hasResults,
        surveyHasDrivers,
        surveyHasKpis,
        resultsBelowThreshold,
        anonymityThreshold,
      }));
    }

    loadSurveys();
    loadAvailability();
    return () => { cancelled = true; };
  }, []);

  return (
    <InsightsNavContext.Provider value={state}>
      {children}
    </InsightsNavContext.Provider>
  );
}
